import type { GameSettings, StationId } from '../game/types';

interface MusicStep {
  semitones: number | null;
  beats: number;
  harmony?: number;
  bass?: boolean;
}

const MUSIC_BEAT_MS = 760;
const MUSIC_BASE_FREQUENCY = 220;
const MUSIC_TRANSPOSITIONS = [0, 5, 3, -2];

// A long, lightly syncopated melody keeps the score calm without sounding like a short jingle.
// Transposing each pass means the exact progression does not repeat for more than two minutes.
const MUSIC_PHRASE: MusicStep[] = [
  { semitones: 0, beats: 1.5, bass: true },
  { semitones: 3, beats: 0.5 },
  { semitones: 5, beats: 1 },
  { semitones: 7, beats: 1.5, harmony: 12 },
  { semitones: null, beats: 0.5 },
  { semitones: 10, beats: 1 },
  { semitones: 7, beats: 1 },
  { semitones: 5, beats: 2 },
  { semitones: 3, beats: 1, bass: true },
  { semitones: 5, beats: 0.5 },
  { semitones: 7, beats: 0.5 },
  { semitones: 12, beats: 1.5, harmony: 7 },
  { semitones: 10, beats: 0.5 },
  { semitones: 7, beats: 1 },
  { semitones: null, beats: 1 },
  { semitones: 5, beats: 2 },
  { semitones: 0, beats: 1, bass: true },
  { semitones: 5, beats: 1 },
  { semitones: 10, beats: 1.5 },
  { semitones: 12, beats: 0.5, harmony: 7 },
  { semitones: 15, beats: 1 },
  { semitones: 12, beats: 1 },
  { semitones: 10, beats: 1 },
  { semitones: 7, beats: 2 },
  { semitones: 5, beats: 1.5, bass: true },
  { semitones: 7, beats: 0.5 },
  { semitones: 10, beats: 1 },
  { semitones: 7, beats: 1 },
  { semitones: 3, beats: 1.5 },
  { semitones: null, beats: 0.5 },
  { semitones: 5, beats: 1 },
  { semitones: 0, beats: 2 },
  { semitones: -2, beats: 1, bass: true },
  { semitones: 3, beats: 1 },
  { semitones: 7, beats: 1.5, harmony: 12 },
  { semitones: 5, beats: 0.5 },
  { semitones: 3, beats: 1 },
  { semitones: 0, beats: 1 },
  { semitones: null, beats: 1 },
  { semitones: 0, beats: 2 }
];

/** Small procedural score and effects layer; everything is generated locally with Web Audio. */
export class AudioManager {
  private context: AudioContext | null = null;
  private musicTimer: number | null = null;
  private ambienceTimer: number | null = null;
  private settings: GameSettings | null = null;
  private musicStep = 0;
  private musicPass = 0;

  async unlock(settings: GameSettings): Promise<void> {
    this.update(settings);
    const context = this.context ?? new AudioContext();
    this.context = context;
    if (context.state === 'suspended') await context.resume();

    // Another lifecycle call may have destroyed this context while resume was pending.
    if (this.context !== context || context.state === 'closed') return;
    this.syncLoops();
  }

  update(settings: GameSettings): void {
    const previous = this.settings;
    const changed = !previous
      || previous.musicVolume !== settings.musicVolume
      || previous.effectsVolume !== settings.effectsVolume
      || previous.ambienceVolume !== settings.ambienceVolume;

    // Game ticks replace the settings object frequently. Ignore those object-identity changes.
    if (!changed) return;
    this.settings = settings;
    if (this.context) this.syncLoops();
  }

  private syncLoops(): void {
    if (!this.context || !this.settings || this.context.state !== 'running') {
      this.stopMusicLoop();
      this.stopAmbienceLoop();
      return;
    }

    if (this.settings.musicVolume > 0) this.ensureMusicLoop();
    else this.stopMusicLoop();

    if (this.settings.ambienceVolume > 0) this.ensureAmbienceLoop();
    else this.stopAmbienceLoop();
  }

  private ensureMusicLoop(): void {
    if (this.musicTimer !== null) return;
    this.scheduleMusicStep(0);
  }

  private scheduleMusicStep(delay: number): void {
    if (this.musicTimer !== null) return;
    this.musicTimer = window.setTimeout(() => {
      this.musicTimer = null;
      if (!this.context || this.context.state !== 'running' || !this.settings || this.settings.musicVolume <= 0) return;
      const nextDelay = this.playMusicStep();
      this.scheduleMusicStep(nextDelay);
    }, delay);
  }

  private stopMusicLoop(): void {
    if (this.musicTimer !== null) window.clearTimeout(this.musicTimer);
    this.musicTimer = null;
  }

  private ensureAmbienceLoop(): void {
    if (this.ambienceTimer !== null) return;
    this.scheduleAmbience(3200 + Math.random() * 2200);
  }

  private scheduleAmbience(delay: number): void {
    if (this.ambienceTimer !== null) return;
    this.ambienceTimer = window.setTimeout(() => {
      this.ambienceTimer = null;
      if (!this.context || this.context.state !== 'running' || !this.settings || this.settings.ambienceVolume <= 0) return;
      this.playAmbience();
      this.scheduleAmbience(6200 + Math.random() * 4800);
    }, delay);
  }

  private stopAmbienceLoop(): void {
    if (this.ambienceTimer !== null) window.clearTimeout(this.ambienceTimer);
    this.ambienceTimer = null;
  }

  private tone(frequency: number, duration: number, volume: number, type: OscillatorType = 'triangle', delay = 0): void {
    if (!this.context || volume <= 0) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    const start = this.context.currentTime + delay;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), start + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.05);
  }

  private playMusicStep(): number {
    if (!this.settings) return MUSIC_BEAT_MS;
    const step = MUSIC_PHRASE[this.musicStep];
    const stepDuration = step.beats * MUSIC_BEAT_MS;
    const volume = this.settings.musicVolume * 0.032;

    if (step.semitones !== null) {
      const transpose = MUSIC_TRANSPOSITIONS[this.musicPass % MUSIC_TRANSPOSITIONS.length];
      const semitones = step.semitones + transpose;
      const frequency = MUSIC_BASE_FREQUENCY * 2 ** (semitones / 12);
      const noteDuration = Math.min(2.2, stepDuration / 1000 * 0.88);
      this.tone(frequency, noteDuration, volume, 'triangle');
      if (step.harmony !== undefined) {
        this.tone(frequency * 2 ** (step.harmony / 12), noteDuration * 0.82, volume * 0.28, 'sine', 0.06);
      }
      if (step.bass) {
        const root = MUSIC_BASE_FREQUENCY * 2 ** (transpose / 12) / 2;
        this.tone(root, Math.min(2.4, noteDuration * 1.35), volume * 0.36, 'sine');
      }
    }

    this.musicStep += 1;
    if (this.musicStep >= MUSIC_PHRASE.length) {
      this.musicStep = 0;
      this.musicPass += 1;
    }
    return stepDuration;
  }

  private playAmbience(): void {
    if (!this.settings) return;
    const volume = this.settings.ambienceVolume * 0.018;
    this.tone(880 + Math.random() * 120, 0.18, volume, 'sine');
    this.tone(1100 + Math.random() * 90, 0.13, volume * 0.8, 'sine', 0.16);
  }

  action(stationId: StationId): void {
    if (!this.settings) return;
    const base = { vineyard: 420, crush: 170, cellar: 250, bottling: 520, sales: 680 }[stationId];
    const volume = this.settings.effectsVolume * 0.07;
    this.tone(base, 0.12, volume, stationId === 'crush' ? 'square' : 'triangle');
    this.tone(base * 1.25, 0.1, volume * 0.7, 'triangle', 0.08);
  }

  success(): void {
    if (!this.settings) return;
    const volume = this.settings.effectsVolume * 0.06;
    [523, 659, 784].forEach((frequency, index) => this.tone(frequency, 0.22, volume, 'triangle', index * 0.09));
  }

  destroy(): void {
    this.stopMusicLoop();
    this.stopAmbienceLoop();
    void this.context?.close();
    this.context = null;
  }
}

export const audioManager = new AudioManager();
